import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCalculos } from "@/hooks/useCalculos";
import { AtualizacaoValoresResult } from "@/types/calculos";
import { useCalculationIndices } from "@/hooks/useIndices";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle } from "lucide-react";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";

export const AtualizacaoValores = () => {
  const [valorOriginal, setValorOriginal] = useState("");
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [indice, setIndice] = useState<'ipca' | 'inpc' | 'tr' | 'selic'>('ipca');
  const [taxaJuros, setTaxaJuros] = useState("");
  const [resultado, setResultado] = useState<AtualizacaoValoresResult | null>(null);
  
  const { calcularAtualizacaoValores, loading } = useCalculos();
  const { indicesData, loading: indicesLoading } = useCalculationIndices();

  useCalculoPrefill("atualizacao", (v) => {
    setValorOriginal(v.valorOriginal ?? "");
    setDataInicial(v.dataInicial ?? "");
    setDataFinal(v.dataFinal ?? "");
    setIndice(v.indice ?? "ipca");
    setTaxaJuros(v.taxaJuros ?? "");
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!valorOriginal || !dataInicial || !dataFinal) {
      return;
    }

    try {
      const result = await calcularAtualizacaoValores({
        valorOriginal: parseFloat(valorOriginal),
        dataInicial,
        dataFinal,
        indice,
        taxaJuros: indice === 'selic' ? 0 : (taxaJuros ? parseFloat(taxaJuros) : 0)
      });
      setResultado(result);
      addToHistory({
        type: "atualizacao",
        label: "Atualização de Valores",
        values: { valorOriginal, dataInicial, dataFinal, indice, taxaJuros },
        summary: `${indice.toUpperCase()} • Valor original ${formatCurrency(parseFloat(valorOriginal))} → Total ${formatCurrency(result.valorTotal)}`,
      });
    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Atualização de Valores</CardTitle>
        <p className="text-sm text-muted-foreground">
          Correção monetária com IPCA, INPC, TR ou SELIC + juros
        </p>
        
        {/* Indicador de Status dos Índices */}
        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium">Status dos Índices Econômicos</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(indicesData).map(([key, data]) => (
              <Badge 
                key={key} 
                variant={data.isUpdated ? "default" : "secondary"} 
                className="text-xs justify-start"
              >
                {data.label}
              </Badge>
            ))}
          </div>
          {indicesLoading && (
            <div className="text-xs text-muted-foreground mt-1">
              Carregando dados atualizados...
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="valor-original">Valor Original</Label>
            <Input
              id="valor-original"
              type="number"
              placeholder="1000.00"
              value={valorOriginal}
              onChange={(e) => setValorOriginal(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="indice">Índice de Correção</Label>
            <Select value={indice} onValueChange={(value: any) => setIndice(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ipca">
                  {indicesData.IPCA?.label || "IPCA"}
                </SelectItem>
                <SelectItem value="inpc">
                  {indicesData.INPC?.label || "INPC"}
                </SelectItem>
                <SelectItem value="tr">
                  {indicesData.TR?.label || "TR"}
                </SelectItem>
                <SelectItem value="selic">
                  {indicesData.SELIC?.label || "SELIC"}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="data-inicial">Data Inicial</Label>
            <Input
              id="data-inicial"
              type="date"
              value={dataInicial}
              onChange={(e) => setDataInicial(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="data-final">Data Final</Label>
            <Input
              id="data-final"
              type="date"
              value={dataFinal}
              onChange={(e) => setDataFinal(e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="taxa-juros">Taxa de Juros (% opcional)</Label>
            <Input
              id="taxa-juros"
              type="number"
              step="0.01"
              placeholder="1.00"
              value={taxaJuros}
              onChange={(e) => setTaxaJuros(e.target.value)}
              disabled={indice === 'selic'}
            />
            {indice === 'selic' && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className="text-sm text-yellow-700">
                  SELIC já inclui correção + juros. Taxa adicional desabilitada conforme Tema 905/STJ.
                </span>
              </div>
            )}
          </div>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
          disabled={loading}
        >
          {loading ? "Calculando..." : "Calcular Atualização"}
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Resultado da Atualização:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Valor Original:</strong> {formatCurrency(parseFloat(valorOriginal))}</p>
                <p><strong>Correção Monetária:</strong> {formatCurrency(resultado.correcaoMonetaria)}</p>
              </div>
              <div>
                <p><strong>Juros:</strong> {formatCurrency(resultado.juros)}</p>
                <p className="text-green-600"><strong>Valor Total:</strong> {formatCurrency(resultado.valorTotal)}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};